import { Sequence } from '@repo/sdk/models/components';

import { ColumnOverlayProperties as OverlayProperties } from '@/components/sequencer-table/column-overlay';
import {
  findColumnWithValue,
  findDuplicateColumns,
  groupIdenticalColumns,
} from '@/components/sequencer-table/validations';

/**
 * The stages of a condenser sequencer.
 */
export enum CondenserSequencerStage {
  UNAVAILABLE = 0,
  OFF = 1,
  ON = 2,
}

/**
 * The display string values of the stages of a condenser sequencer.
 */
const CONDENSER_SEQUENCER_STAGE_STRING_VALUES = ['Unavailable', 'Off', 'On'];

/**
 * Derives the icon for a row in a condenser sequencer. We cannot do this in BFF,
 * because we need to know the actual value of the narrative var
 *
 * @param name - The name of the condenser sequencer.
 * @returns The icon for the condenser sequencer.
 */
export const deriveCondenserSequencerIcon = (name: string) => {
  return name.includes('fan') ? 'fan' : name.includes('compressor') ? 'compressor' : 'pump';
};

/**
 * Returns the string value of the condenser sequencer stage.
 *
 * @param val - The stage value to get the string value for.
 * @returns The string value of the condenser sequencer stage.
 */
export const getCondenserSequenceState = (stageValue: number): string => {
  if (stageValue === CondenserSequencerStage.OFF) {
    return CONDENSER_SEQUENCER_STAGE_STRING_VALUES[CondenserSequencerStage.OFF];
  }
  if (stageValue === CondenserSequencerStage.ON) {
    return CONDENSER_SEQUENCER_STAGE_STRING_VALUES[CondenserSequencerStage.ON];
  }
  return CONDENSER_SEQUENCER_STAGE_STRING_VALUES[CondenserSequencerStage.UNAVAILABLE];
};

/**
 * Returns the cell styles for the condenser sequencer stage.
 * @param stageValue - The stage value to get the cell styles for.
 * @returns The cell styles for the condenser sequencer stage.
 */
export const getCondenserSequencerCellStyles = (
  stageValue: number
): { className: string; style: React.CSSProperties } => {
  return {
    className: ['bg-muted', 'bg-muted', 'bg-primary'][stageValue],
    style: {},
  };

  // Saving this for when Compressors happen, it goes in the style. It won't be in this file but I want ot keep the code around.
  // const trimBackground = `linear-gradient(136deg, #46d401 10%, #adff00 10%, #adff00 20%, #46d401 20%, #46d401 30%, #adff00 30%, #adff00 40%, #46d401 40%, #46d401 50%, #adff00 50%, #adff00 60%, #46d401 60%, #46d401 70%, #adff00 70%, #adff00 80%, #46d401 80%, #46d401 90%, #adff00 90%, #adff00 100%)`;
};

/**
 * Returns the content of the cell for a condenser sequencer stage.
 *
 * @param stageValue - The stage value to get the cell content for.
 * @returns The content of the cell for a condenser sequencer stage.
 */
export const getCondenserSequencerCellContent = (stageValue: number): React.ReactNode => {
  switch (stageValue) {
    case CondenserSequencerStage.UNAVAILABLE:
      return null; // LockIcon is rendered in the editor component
  }
  return null;
};

/**
 * Cycles the stage value of a condenser sequencer.
 *
 * @param currentStageValue - The current stage value to cycle.
 * @returns The new stage value.
 */
export const cycleCondenserSequencerStageValue = (currentStageValue: number): number => {
  switch (currentStageValue) {
    case CondenserSequencerStage.OFF:
      return CondenserSequencerStage.ON;
    case CondenserSequencerStage.ON:
      return CondenserSequencerStage.OFF;
  }
  return currentStageValue;
};

/**
 * Validates a condenser sequencer and returns overlay properties for each column.
 * This function checks for empty columns and duplicate columns.
 *
 * Priority order (higher priority overrides lower):
 * 1. Duplicates
 * 2. Empty (highest priority)
 *
 * @param sequence - The sequence to validate
 * @returns Array of overlay properties for each column
 */
export const getCondenserSequencerOverlayProperties = (
  sequence: Sequence | undefined
): (OverlayProperties | null)[] => {
  if (!sequence) {
    return [];
  }

  const maxStages = Math.max(...(sequence?.table?.map((item) => item.stageValues.length) || [0]));
  const overlays: (OverlayProperties | null)[] = Array.from({ length: maxStages }).map(() => null);

  // Find duplicate columns
  const duplicateColumns = findDuplicateColumns(sequence);
  const duplicateGroups = groupIdenticalColumns(sequence);

  // Mark duplicate columns (but not the first in each group)
  duplicateColumns.forEach((colIndex: number) => {
    const group = duplicateGroups.find((g) => g.includes(colIndex));
    // Skip the first column in the group (it's the "original")
    if (group && group[0] === colIndex) {
      return;
    }
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: `Same as Stage ${group?.[0]}`,
    };
  });

  // Find empty columns (this overrides duplicates as it's more severe)
  const emptyColumns = findColumnWithValue(sequence, [
    CondenserSequencerStage.OFF,
    CondenserSequencerStage.UNAVAILABLE,
  ]);

  emptyColumns.forEach((colIndex: number) => {
    // Don't mark the 0th column as empty (it's always empty by design)
    if (colIndex === 0) {
      return;
    }
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: 'Empty',
    };
  });

  return overlays;
};
